import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "./ui/button";
import { getMarketingUi } from "../content/marketingUi";
import { getConsentStatus, setAccepted, setRejected } from "../lib/cookieConsent";
import { MARKETING_CONSENT_EVENT, disableMetaPixel, initMetaPixel } from "../lib/metaPixel";

// Firebase loads on demand (main.tsx starts it after the page is up), so the
// marketing pages do not ship the SDK in their first bundle.
const loadFirebase = () => import("../config/firebase");

interface CookieConsentBannerProps {
  language: string;
  /** Bump to show the banner again (footer "Cookie preferences"). */
  reopenSignal?: number;
}

/**
 * Cookie consent banner — KVKK + GDPR.
 * KVKK Article 5 + GDPR Article 7(3) require that withdrawing/refusing
 * consent be as easy as giving it. We therefore expose Reject as a
 * peer button (equal weight) rather than hidden behind an X close.
 */
export function CookieConsentBanner({ language, reopenSignal = 0 }: CookieConsentBannerProps) {
  const ui = getMarketingUi(language);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Show banner whenever consent is unknown (neither accepted nor rejected).
    // Status helper handles localStorage unavailability — defaults to "unknown".
    // Read after mount, not during render: marketing pages are prerendered
    // without localStorage and would ship the banner to everyone.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (getConsentStatus() === "unknown") setShow(true);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (reopenSignal > 0) setShow(true);
  }, [reopenSignal]);

  const handleAccept = () => {
    setAccepted();
    // Spin up Analytics + Performance and the Meta Pixel now that we have
    // consent; boot-time init skipped them because hasMarketingConsent() was false.
    loadFirebase()
      .then((m) => {
        m.initializeFirebase();
        m.enableConsentedAnalytics();
      })
      .catch(() => {});
    if (initMetaPixel()) window.dispatchEvent(new Event(MARKETING_CONSENT_EVENT));
    setShow(false);
  };

  const handleReject = () => {
    // Required by KVKK + GDPR: rejection must be as easy as acceptance.
    // Closing the banner without persisting was a previous bug (re-prompt
    // every page load) — now we persist a "rejected" status and tear down
    // any tracking that may have started in a prior accepted session.
    setRejected();
    loadFirebase().then((m) => m.disableConsentedAnalytics()).catch(() => {});
    disableMetaPixel();
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/97 px-4 py-3 shadow-lg backdrop-blur-md sm:px-6"
      role="dialog"
      aria-live="polite"
      aria-label={ui.cookiePreferences}
    >
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Cookie className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <p className="text-xs text-muted-foreground">
            {ui.cookieText}{" "}
            <Link to="/privacy-policy" className="text-primary underline hover:text-primary/80">
              {ui.privacyPolicy}
            </Link>
            {language === "tr" && (
              <>
                {" · "}
                <Link to="/privacy-policy#kvkk" className="text-primary underline hover:text-primary/80">
                  KVKK Aydınlatma Metni
                </Link>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={handleReject} className="text-xs">
            {ui.reject}
          </Button>
          <Button size="sm" onClick={handleAccept} className="text-xs">
            {ui.accept}
          </Button>
        </div>
      </div>
    </div>
  );
}
